import React, { useRef } from "react";
import "./detailPreliquidationInvoice.scss";
import { Form, FormSpy } from "react-final-form";
import { BasicIcon, Tooltip, ToolTipPosition } from "@pickit/pickit-components";
import { PdfViewer } from "component/pdf-viewer/PdfViewer";
import { InvoiceFormType, detailPreliquidationInvoicePropsType } from "./types";
import useValidationSchema from "hooks/useValidationSchema";
import PdfController from "component/pdfController/PdfController";
import { TabControler } from "component/TabControler/TabControler";
import Back from "component/back/Back";
import { PagesPreliquidationTypes } from "../types";
import LoadingButton from "component/loadingButton/LoadingButton";
import validator from "utils/validator";
import InvoiceDataAR from "./invoiceData/AR/InvoiceDataAR";
import GeneralData from "./generalData/GeneralData";
import InvoiceDataCO from "./invoiceData/CO/InvoiceDataCO";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import "./i18n";
import InvoiceDataMX from "./invoiceData/MX/InvoiceDataMX";
import { CountryComponentType } from "utils/types";
import { PRELIQUIDATION_STATUS, PRELIQUIDATION_STATUS_NAMES } from "utils/constants";
import InvoiceDataCL from "./invoiceData/CL/InvoiceDataCL";

const separatorIndexes = [8, 13, 18, 23];

export const Invoice: React.FC<detailPreliquidationInvoicePropsType> = ({
  invoiceFileStatus,
  presettlementId,
  minDate,
  invoiceDetail,
  detailPreliquidations,
  validationSchema,
  invoiceTypes,
  initialValues,
  isFormEnabled,
  isFormEnabledCL,
  approvingInvoice,
  keepDirty,
  tabs,
  countryCode,
  disableDeleteReplaceIcon,
  disableRejectInvoice,
  isBulkFlow,
  setDirty,
  getInvoiceDetailSave,
  getInvoiceDetailApprove,
  fileHandler,
  deleteFile,
  downloadFile,
  goToPreviousFile,
  handleClickBack,
  changePage,
  toggleRejectInvoiceModalVisibility,
  testId,
}): JSX.Element => {
  const pdfControllerRef = useRef<any>();
  const { t } = useTranslation();
  const isPending = detailPreliquidations.status !== PRELIQUIDATION_STATUS.PENDING;

  const invoiceData: CountryComponentType = {
    AR: (
      <InvoiceDataAR
        isFormEnabled={isFormEnabled}
        minDate={minDate}
        invoiceTypes={invoiceTypes}
        testId={`${testId}-data`}
      />
    ),
    CO: (
      <InvoiceDataCO
        isFormEnabled={isFormEnabled}
        minDate={minDate}
        invoiceTypes={invoiceTypes}
        testId={`${testId}-data`}
      />
    ),
    MX: (
      <InvoiceDataMX
        testId={`${testId}-data`}
        isFormEnabled={isFormEnabled}
        minDate={minDate}
      />
    ),
    CL: (
      <InvoiceDataCL
        testId={`${testId}-data`}
        isFormEnabled={isFormEnabled}
        isFormEnabledCL={isFormEnabledCL}
        minDate={minDate}
        invoiceTypes={invoiceTypes}
      />
    ),
  };

  return (
    <div>
      <Form
        onSubmit={(value) => value}
        initialValues={initialValues}
        keepDirtyOnReinitialize={isBulkFlow ? false : keepDirty}
        mutators={{
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
          setMXInvoice: ([field, value], state, { changeValue }) => {
            if (separatorIndexes.includes(value.length)) {
              changeValue(state, field, () => value + "-");
            }
            if (
              separatorIndexes.includes(value.length - 1) &&
              value.slice(-1) !== "-"
            ) {
              const newValue =
                value.slice(0, value.length - 1) + "-" + value.slice(-1);
              changeValue(state, field, () => newValue);
            }
          },
        }}
        validate={useValidationSchema(validationSchema(false))}
      >
        {({ invalid, handleSubmit, form, values, dirty }) => (
          <form className="form-filter-transaction" onSubmit={handleSubmit}>
            <div className="header-container">
              <TabControler<PagesPreliquidationTypes>
                tabs={tabs}
                changePage={(page) => {
                  changePage(page, dirty);
                }}
                actualPage={"invoice"}
                clickable={false}
              />
              <Back
                testId="invoice-button-back"
                id="invoice-button-back"
                onClick={() => handleClickBack(dirty)}
              />
            </div>
            <div
              className="detail-preliquidation-number-container"
              id="invoice-number"
            >
              <h2
                data-testId={`${testId}-presettlement-number-title`}
                className="detail-preliquidation-h2"
              >
                {t("detailPresettlement:presettlementNumber")}
              </h2>
              <p
                data-testId={`${testId}-presettlement-number`}
                className="detail-preliquidation-number"
              >
                {presettlementId}
              </p>
            </div>

            <FormSpy
              subscription={{
                values: true,
                dirty: true,
                invalid: true,
              }}
              onChange={(pro) => {
                setDirty(pro.dirty);
              }}
            />
            <div className="container-detail-preliquidation form-detail-preliquidation">
              <div className="invoice-title-container">
                <h3
                  data-testId={`${testId}-subtitle-pending-data`}
                  className="subTitle-pending-data"
                >
                  {t("invoice:invoiceData")}
                </h3>
                <p
                  data-testId={`${testId}-status`}
                  className="invoice-status-container"
                >
                  {t("invoice:status")}:
                  <span
                    className={classNames("invoice-status", {
                      "invoice-status-approved":
                        detailPreliquidations.status === PRELIQUIDATION_STATUS.APPROVED,
                    })}
                  >
                    {t(
                      `${
                        PRELIQUIDATION_STATUS_NAMES[
                          detailPreliquidations.status
                        ]
                      }`
                    )}
                  </span>
                </p>
              </div>

              <div className="container-detail-preliquidation-row container-invoice-first-row">
                <div
                  data-testId={`${testId}-country-code`}
                  className="container-detail-preliquidation-col-sm-5 form-part-1-admin-pickers detail-preliquidation-padding-right"
                >
                  {invoiceData[countryCode]}
                </div>
                <div className="container-detail-preliquidation-col-sm-2 detail-preliquidation-adjust">
                  <PdfController
                    ref={pdfControllerRef}
                    title={t("invoice:invoice")}
                    buttonText={
                      invoiceDetail.invoiceFile?.upload &&
                      invoiceFileStatus.error
                        ? t("invoice:previousInvoice")
                        : t("invoice:uploadInvoice")
                    }
                    fileUploaded={invoiceDetail.invoiceFile?.upload}
                    showError={invoiceFileStatus.error}
                    errorMessage={t(invoiceFileStatus.message || "")}
                    loading={invoiceFileStatus.loading}
                    fileHandler={fileHandler}
                    disabled={isPending || (!isFormEnabled && !invoiceFileStatus.error && invoiceDetail?.invoiceFile?.upload)}
                    goToPreviousFile={goToPreviousFile}
                  >
                    <PdfViewer src={invoiceDetail.invoiceFile?.url || ""}>
                      <Tooltip
                        disabled={disableDeleteReplaceIcon}
                        message={t("global:delete")}
                        position={ToolTipPosition.top}
                      >
                        <button
                          data-testId={`${testId}-delete-pdf-button`}
                          className="icon-container-primary"
                          type="button"
                          disabled={disableDeleteReplaceIcon}
                          onClick={deleteFile}
                        >
                          <BasicIcon name="trash" size="small" />
                        </button>
                      </Tooltip>
                      <Tooltip
                        disabled={disableDeleteReplaceIcon}
                        message={t("global:replace")}
                        position={ToolTipPosition.top}
                      >
                        <button
                          data-testId={`${testId}-replace-pdf-button`}
                          className="icon-container-primary"
                          type="button"
                          disabled={disableDeleteReplaceIcon}
                          onClick={() =>
                            pdfControllerRef?.current?.triggerOnChange()
                          }
                        >
                          <BasicIcon name="switch-vertical" size="small" />
                        </button>
                      </Tooltip>
                      <Tooltip
                        disabled={!invoiceDetail.invoiceFile?.upload}
                        message={t("global:download")}
                        position={ToolTipPosition.top}
                      >
                        <button
                          data-testId={`${testId}-download-pdf-button`}
                          disabled={!invoiceDetail.invoiceFile?.upload}
                          type="button"
                          className="icon-container-secondary"
                          onClick={downloadFile}
                        >
                          <BasicIcon
                            name="download"
                            color="#FFFFFF"
                            size="small"
                          />
                        </button>
                      </Tooltip>
                    </PdfViewer>
                  </PdfController>
                </div>
              </div>
              <h3 className="subTitle-pending-data detail-preliquidation-margin-top">
                {t("invoice:generalData")}
              </h3>
              <div className="form-part-1-admin-pickers detail-preliquidation-margin-top">
                <GeneralData
                  testId="general-data"
                  pickerId={detailPreliquidations.pickerId}
                  countryCode={countryCode}
                />
              </div>
            </div>
            <div className="detail-preliquidation-buttons">
              <button
                type="button"
                disabled={!isFormEnabled || !dirty}
                className="button-submit-subtype"
                data-testId={`${testId}-submit-button`}
                onClick={() =>
                  validator({
                    values,
                    validationSchema: validationSchema(true),
                    onClick: () =>
                      getInvoiceDetailSave({
                        ...(values as InvoiceFormType),
                        presettlementId,
                      }),
                  })
                }
              >
                {t("global:save")}
              </button>
              <div className="detail-preliquidation-fondo">
                <button
                  data-testId={`${testId}-reject-invoice-button`}
                  disabled={
                    disableRejectInvoice(detailPreliquidations.status !== PRELIQUIDATION_STATUS.IN_APPROVEMENT && invalid)
                  }
                  className="detail-preliquidation-invoice-p reject-invoice"
                  type="button"
                  onClick={() => toggleRejectInvoiceModalVisibility(true)}
                >
                  <p>{t("invoice:refuse")}</p>
                </button>
                <Tooltip
                  message={t("invoice:missingDataTooltip")}
                  position={ToolTipPosition.top}
                  disabled={
                    !!initialValues.fiscalData.fiscalNumber ||
                    detailPreliquidations.status !== PRELIQUIDATION_STATUS.IN_APPROVEMENT
                  }
                >
                  <LoadingButton
                    testId={`${testId}-approve-button`}
                    disabled={
                      invoiceFileStatus.error ||
                      invalid ||
                      !invoiceDetail.invoiceFile?.upload ||
                      detailPreliquidations.status !== PRELIQUIDATION_STATUS.IN_APPROVEMENT ||
                      !initialValues.fiscalData.fiscalNumber
                    }
                    className="button-approve-invoice"
                    status={approvingInvoice}
                    onClick={() =>
                      getInvoiceDetailApprove({
                        ...(values as InvoiceFormType),
                        presettlementId,
                      })
                    }
                  >
                    {t("invoice:approve")}
                  </LoadingButton>
                </Tooltip>
              </div>
            </div>
          </form>
        )}
      </Form>
    </div>
  );
};

export default Invoice;

